import React, { useEffect, useState } from "react";
import {
  Modal,
  Row,
  Col,
  Card,
  Select,
  Divider,
  Spin,
  Typography,
  Alert,
  Button,
  Table,
  Checkbox,
  Tooltip,
  message,
  Grid,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import {
  fetchLeadSourceApi,
  fetchLeadStatusApi,
  fetchLeadPurposeApi,
  saveMetaFormConfigurationApi,
  fetchMetaFormConfigurationApi,
} from "../../../../apiservice/crm";
import { fetchEmployeeUsers } from "../../../Employees/employeeThunks";
import { useDispatch, useSelector } from "react-redux";
import { isAdmin } from "../../../../lib/functions";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import "../../crmSharedStyle.css";
const { Title, Text } = Typography;

const MetaFormConfigModal = ({ open, form, onClose, onSaved, users }) => {
  const screens = Grid.useBreakpoint();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [crmFields, setCrmFields] = useState([]);
  const [configuration, setConfiguration] = useState({
    lead_source_id: null,
    status_id: null,
    purpose_id: null,
    assigned_to: null,
  });
  const [mappings, setMappings] = useState([]);

  const authInfo = useSelector(selectAuthInfo);
  const adminUser = isAdmin(authInfo);

  const currentUserId = authInfo?.userInfo?.user_id;
  const currentUserName = authInfo?.userInfo?.full_name;

  const loadMasters = async () => {
    try {
      const [sourceRes, statusRes, purposeRes] = await Promise.all([
        fetchLeadSourceApi(),
        fetchLeadStatusApi(),
        fetchLeadPurposeApi(),
      ]);
      setSources(sourceRes.data || []);
      setStatuses(statusRes.data || []);
      setPurposes(purposeRes.data || []);
      dispatch(fetchEmployeeUsers());
    } catch (err) {
      console.log(err);
    }
  };

  const loadConfiguration = async () => {
    if (!form?.form_id) return;
    try {
      setLoading(true);
      const res = await fetchMetaFormConfigurationApi(form.form_id);
      const data = res.data;
      setQuestions(data.meta_questions || []);
      setCrmFields(data.crm_fields || []);
      setConfiguration({
        lead_source_id: data.configuration?.lead_source_id || null,
        status_id: data.configuration?.status_id || null,
        purpose_id: data.configuration?.purpose_id || null,
        assigned_to: data.configuration?.assigned_to || null,
      });
      // Prefer the full per-field rows (carries add_to_notes + label);
      // fall back to the older flat field_mapping shape for safety.
      const savedMappings = Array.isArray(data.configuration?.mappings)
        ? data.configuration.mappings
        : Object.entries(data.configuration?.field_mapping || {}).map(
            ([meta_key, crm_key]) => ({ meta_key, crm_key })
          );

      setMappings(savedMappings);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMasters();
  }, []);

  useEffect(() => {
    if (open) {
      loadConfiguration();
    }
  }, [open, form]);

  const getMappedValue = (metaKey) => {
    const row = mappings.find((x) => x.meta_key === metaKey);
    return row?.crm_key;
  };

  const getAddToNotes = (metaKey) => {
    const row = mappings.find((x) => x.meta_key === metaKey);
    return !!row?.add_to_notes;
  };

  // Merges a patch (crm_key and/or add_to_notes) into a field's mapping
  // row. The two are independent: checking "Add to Notes" never clears or
  // disables the CRM field mapping, and vice versa — a question can be
  // mapped, noted, both, or neither.
  const updateMapping = (metaKey, label, patch) => {
    setMappings((prev) => {
      const list = [...prev];
      const index = list.findIndex((x) => x.meta_key === metaKey);
      if (index > -1) {
        list[index] = { ...list[index], label, ...patch };
      } else {
        list.push({ meta_key: metaKey, label, crm_key: null, add_to_notes: false, ...patch });
      }
      return list;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveMetaFormConfigurationApi({
        form_id: form.form_id,
        defaults: {
          lead_source_id: configuration.lead_source_id,
          status_id: configuration.status_id,
          purpose_id: configuration.purpose_id,
          assigned_to: configuration.assigned_to,
        },
        mappings,
      });
      message.success("Configuration saved successfully");
      onSaved?.();
      onClose();
    } catch (err) {
      console.log(err);
      message.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleAutoMap = () => {

  const aliases = {
    fullname: "name",
    full_name: "name",
    name: "name",

    email: "email",

    phone: "contact_no",
    phonenumber: "contact_no",
    mobile: "contact_no",

    company: "institution_name",
    companyname: "institution_name",

    address: "address",

    country: "country",

    profession: "profession",
  };

  // Merge onto the existing rows rather than replacing them outright, so
  // Auto Map never wipes out a manual CRM-field pick or an "Add to Notes"
  // choice on a question it doesn't recognise.
  setMappings((prev) => {
    const list = [...prev];

    questions.forEach((question) => {
      const key = question.label
        .toLowerCase()
        .replace(/[\s_-]/g, "");

      const crmKey = aliases[key];
      if (!crmKey) return;

      const index = list.findIndex((x) => x.meta_key === question.key);
      if (index > -1) {
        list[index] = { ...list[index], crm_key: crmKey, label: question.label };
      } else {
        list.push({
          meta_key: question.key,
          crm_key: crmKey,
          add_to_notes: false,
          label: question.label,
        });
      }
    });

    return list;
  });
};

  const columns = [
    {
      title: "Facebook Field",
      dataIndex: "label",
      width: 260,
      render: (_, record) => (
        <div>
          <div
            style={{
              fontWeight: 600,
              color:
                getMappedValue(record.key) || getAddToNotes(record.key)
                  ? "#000"
                  : "#d4380d",
            }}
          >
            {record.label}
          </div>

          <div
            style={{
              fontSize: 12,
              color: "#888",
            }}
          >
            {record.key}
          </div>
        </div>
      ),
    },

    {
      title: "CRM Field",
      width: 240,
      render: (_, record) => {
        const usedFields = mappings
          .filter((x) => x.meta_key !== record.key)
          .map((x) => x.crm_key);

        return (
          <Select
            style={{ width: "100%" }}
            placeholder="Select CRM Field"
            showSearch
            allowClear
            optionFilterProp="label"
            value={getMappedValue(record.key)}
            options={crmFields
              .filter(
                (field) =>
                  !usedFields.includes(field.field_key) ||
                  field.field_key === getMappedValue(record.key)
              )
              .map((field) => ({
                label: field.field_name,
                value: field.field_key,
              }))}
            onChange={(value) =>
              updateMapping(record.key, record.label, { crm_key: value ?? null })
            }
          />
        );
      },
    },

    {
      title: (
        <span>
          Add to Notes{" "}
          <Tooltip title="Save this question's answer as free text on the lead's activity notes — independent of the CRM Field mapping. Useful for questions with no matching field, and can be used together with a mapping.">
            <InfoCircleOutlined style={{ color: "#999" }} />
          </Tooltip>
        </span>
      ),
      width: 120,
      align: "center",
      render: (_, record) => (
        <Checkbox
          checked={getAddToNotes(record.key)}
          onChange={(e) =>
            updateMapping(record.key, record.label, { add_to_notes: e.target.checked })
          }
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title="Configure Meta Lead Form"
      onCancel={onClose}
      width={screens.md ? 950 : "95%"}
      footer={null}
      destroyOnClose
      className="crm-modal-responsive"
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <Alert
            showIcon
            type="info"
            message={form?.form_name}
            description="Configure default lead values and map Facebook form fields to CRM lead fields. Unmapped fields will be ignored."
          />
          <Divider>Lead Defaults</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Text>Lead Source</Text>
              <Select
                style={{ width: "100%" }}
                value={configuration.lead_source_id}
                options={sources.map((x) => ({
                  label: x.name,
                  value: x.id,
                }))}
                onChange={(value) => {
                  setConfiguration((prev) => ({
                    ...prev,
                    lead_source_id: value,
                  }));
                }}
              />
            </Col>

            <Col span={12}>
              <Text>Lead Status</Text>

              <Select
                style={{ width: "100%" }}
                value={configuration.status_id}
                options={statuses.map((x) => ({
                  label: x.name,
                  value: x.id,
                }))}
                onChange={(value) => {
                  setConfiguration((prev) => ({
                    ...prev,
                    status_id: value,
                  }));
                }}
              />
            </Col>

            <Col span={12}>
              <Text>Lead Purpose</Text>

              <Select
                style={{ width: "100%" }}
                allowClear
                value={configuration.purpose_id}
                options={purposes.map((x) => ({
                  label: x.name,
                  value: x.id,
                }))}
                onChange={(value) => {
                  setConfiguration((prev) => ({
                    ...prev,

                    purpose_id: value,
                  }));
                }}
              />
            </Col>

            <Col span={12} style={{ marginTop: 16 }}>
              <Text>Assign Agent</Text>
              <Select
                style={{ width: "100%" }}
                allowClear
                showSearch
                value={configuration.assigned_to}
                options={
                    adminUser
                    ? (users || []).map((u) => ({
                        label: u.full_name,
                        value: u.id,
                        }))
                    : [
                        {
                            label: currentUserName,
                            value: currentUserId,
                        },
                        ]
                }
                onChange={(value) => {
                  setConfiguration((prev) => ({
                    ...prev,
                    assigned_to: value,
                  }));
                }}
              />
            </Col>
          </Row>

          <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "20px 0 10px",
            }}
            >
            <div>
                <b>Facebook → CRM Field Mapping</b>

                <span
                style={{
                    marginLeft: 10,
                    color: "#888",
                }}
                >
                {mappings.filter((x) => x.crm_key).length} mapped to a field · {" "}
                {mappings.filter((x) => x.add_to_notes).length} saved to notes · {" "}
                {questions.length} question{questions.length === 1 ? "" : "s"} total
                </span>
            </div>

            <Button
                size="small"
                onClick={handleAutoMap}
            >
                Auto Map
            </Button>
            </div>

          <Table
            rowKey="key"
            columns={columns}
            dataSource={questions}
            pagination={false}
            size="small"
            scroll={{
              y: 350,
            }}
          />

          {(() => {
            const unhandled = questions.filter(
              (q) => !getMappedValue(q.key) && !getAddToNotes(q.key)
            );
            if (!unhandled.length) return null;
            return (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message={`${unhandled.length} question${unhandled.length === 1 ? "" : "s"} won't be saved anywhere`}
                description={`Map them to a CRM field or check "Add to Notes" so nothing a lead answers is lost: ${unhandled
                  .map((q) => q.label)
                  .join(", ")}`}
              />
            );
          })()}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 24,
              gap: 10,
            }}
          >
            <Button onClick={onClose}>Cancel</Button>

            <Button type="primary" loading={saving} onClick={handleSave}>
              Save Configuration
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default MetaFormConfigModal;
